// ReportContext.tsx — Context provider with report-wide state
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { ChartComponent } from "@syncfusion/ej2-react-charts";
import type { AnalyticsDataset, AnalyticsSeries } from "../types/analytics";
import { apiService } from "../api/client";
import { normalizeAnalyticsKey } from "../utils/analyticsKeys";
import { parseScopeCenterPoint } from "../utils/scopeLocation";

export interface ReportColumn {
    field: string;
    headerName: string;
    type: string;
    filterable: boolean;
}

export interface DeviceLocationType {
    hasLocation: boolean;
    longKeyColumn: string | null;
    latKeyColumn: string | null;
    long: number | null;
    lat: number | null;
    name: string | null;
}

export interface DeviceVoltageType {
    hasVoltage: boolean;
    voltageKeyColumn: string | null;
    latest: number | null;
    min: number | null;
    max: number | null;
    successReadings: number;
    failReadings: number;
    failRangeReadings: number;
    avgGood: number | null;
    deviation: number | null;
    totalReadings: number;
    successPercentage: number;
    failPercentage: number;
    failRangePercentage: number;
}

export interface DeviceWeatherType {
    hasWeather: boolean;
    tempKeyColumn: string | null;
    rainfallKeyColumn: string | null;
    windSpeedKeyColumn: string | null;
    windAngleKeyColumn: string | null;
    windDirectionKeyColumn: string | null;
    temp: number | null;
    rainfall: number | null;
    windSpeed: number | null;
    windAngle: number | null;
    windDirection: string | null;
}

export interface DeviceConsumptionType {
    hasConsumption: boolean;
    chartRef: React.RefObject<ChartComponent | null>;
    consumptionNowKeyColumn: string | null;
    consumptionHourKeyColumn: string | null;
    consumptionDayKeyColumn: string | null;
    consumptionMonthKeyColumn: string | null;
    currentKeyColumn: string | null;
    consumptionNow: number | null;
    consumptionHour: number | null;
    consumptionDay: number | null;
    consumptionMonth: number | null;
    current: number | null;
}

export interface DevicePowerType {
    hasPower: boolean;
    chartRef: React.RefObject<ChartComponent | null>;
    engineNumberKeyColumn: string | null;
    runningKeyColumn: string | null;
    fuelUsedKeyColumn: string | null;
    fuelRemainingKeyColumn: string | null;
    runtimeEstimateKeyColumn: string | null;
    engineNumber: number | null;
    running: boolean | null;
    fuelUsed: number | null;
    fuelRemaining: number | null;
    runtimeEstimateHours: number | null;
}

export interface DeviceHealthType {
    hasHealth: boolean;
    healthKeyColumn: string | null;
    latest: number | null;
    totalReadings: number;
    successReadings: number;
    failReadings: number;
    unavailableReadings: number;
    offlineReadings: number;
}

export type ScopeHierarchy = "devices" | "venues";
export type ScopeLevel =
    | "categories"
    | "templates"
    | "instances"
    | "monitors"
    | "venues"
    | "events"
    | "zones"
    | "virtual_devices";

export interface ScopeOption {
    id: number;
    label: string;
    secondaryLabel?: string | null;
    resolvedSource?: ReportResolvedSource;
}

export interface ReportResolvedSource {
    tableName: "instances" | "monitors";
    id: number;
    label: string;
    rowContext?: Record<string, unknown>;
}

export interface SelectionPrototypeSortField {
    field: string;
    direction: "Ascending" | "Descending";
}

export interface SelectionPrototypeState {
    hierarchy: ScopeHierarchy;
    level: ScopeLevel;
    selectedOptions: ScopeOption[];
    resolvedSources: ReportResolvedSource[];
    expandedIds: string[];
    datePreset: "today" | "yesterday" | "weekToDate" | "lastWeek" | "monthToDate" | "lastMonth" | "yearToDate" | "custom" | "all";
    dateStart: string | null;
    dateEnd: string | null;
    includedFields: string[];
    exprField: string;
    exprOp: string;
    exprValue: string;
    sortFields: SelectionPrototypeSortField[];
}

export interface SharePrototypeState {
    destinationCategory: "document" | "data" | "publication";
    documentFormat: "pdf" | "md" | "docx";
    dataFormat: "csv" | "xlsx" | "json";
    publicationDestination: "postgres_view" | "export_feed" | "power_bi" | "tableau";
    publicationName: string;
    recurrenceMode: "once" | "weekly";
    recurrenceDay: "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";
}

export const DEFAULT_SELECTION_PROTOTYPE_STATE: SelectionPrototypeState = {
    hierarchy: "devices",
    level: "categories",
    selectedOptions: [],
    resolvedSources: [],
    expandedIds: [],
    datePreset: "all",
    dateStart: null,
    dateEnd: null,
    includedFields: [],
    exprField: "",
    exprOp: "equal",
    exprValue: "",
    sortFields: [],
};

export const DEFAULT_SHARE_PROTOTYPE_STATE: SharePrototypeState = {
    destinationCategory: "document",
    documentFormat: "pdf",
    dataFormat: "csv",
    publicationDestination: "postgres_view",
    publicationName: "",
    recurrenceMode: "once",
    recurrenceDay: "monday",
};

interface ReportContextType {
    tableName: string;
    setTableName: (tableName: string) => void;
    keyProperty: string;
    setKeyProperty: (keyProperty: string) => void;
    filters: any[];
    setFilters: (filters: any[]) => void;
    visibleColumns: string[];
    setVisibleColumns: (columns: string[]) => void;
    view: string;
    setView: (view: string) => void;
    reportTab: string;
    setReportTab: (tab: string) => void;
    reportData: any;
    setReportData: (data: any[]) => void;
    columns: ReportColumn[];
    setColumns: (columns: ReportColumn[]) => void;
    analyticsDataset: AnalyticsDataset | null;
    setAnalyticsDataset: (dataset: AnalyticsDataset | null) => void;
    analyticsDatasetLoading: boolean;
    setAnalyticsDatasetLoading: (loading: boolean) => void;
    analyticsDatasetError: string | null;
    setAnalyticsDatasetError: (error: string | null) => void;
    analyticsWeatherDataset: AnalyticsDataset | null;
    setAnalyticsWeatherDataset: (dataset: AnalyticsDataset | null) => void;
    analyticsWeatherDatasetLoading: boolean;
    setAnalyticsWeatherDatasetLoading: (loading: boolean) => void;
    analyticsWeatherDatasetError: string | null;
    setAnalyticsWeatherDatasetError: (error: string | null) => void;
    analyticsRollupDataset: AnalyticsDataset | null;
    setAnalyticsRollupDataset: (dataset: AnalyticsDataset | null) => void;
    analyticsRollupDatasetLoading: boolean;
    setAnalyticsRollupDatasetLoading: (loading: boolean) => void;
    analyticsRollupDatasetError: string | null;
    setAnalyticsRollupDatasetError: (error: string | null) => void;
    analyticsGroupedDataset: AnalyticsDataset | null;
    setAnalyticsGroupedDataset: (dataset: AnalyticsDataset | null) => void;
    analyticsGroupedDatasetLoading: boolean;
    setAnalyticsGroupedDatasetLoading: (loading: boolean) => void;
    analyticsGroupedDatasetError: string | null;
    setAnalyticsGroupedDatasetError: (error: string | null) => void;
    analyticsGroupedQuery: {
        metric: string;
        group_by: "instance" | "template" | "status";
        interval: "hour" | "day" | "month";
        aggregation: "latest" | "avg" | "min" | "max" | "sum" | "delta";
    };
    setAnalyticsGroupedQuery: React.Dispatch<React.SetStateAction<{
        metric: string;
        group_by: "instance" | "template" | "status";
        interval: "hour" | "day" | "month";
        aggregation: "latest" | "avg" | "min" | "max" | "sum" | "delta";
    }>>;
    groupedPowerMetric: string;
    setGroupedPowerMetric: React.Dispatch<React.SetStateAction<string>>;

    deviceLocation: DeviceLocationType | null;
    setDeviceLocation: (location: DeviceLocationType | null) => void;

    deviceVoltage: DeviceVoltageType | null;
    setDeviceVoltage: (voltage: DeviceVoltageType | null) => void;

    deviceWeather: DeviceWeatherType | null;
    setDeviceWeather: (weather: DeviceWeatherType | null) => void;

    deviceConsumption: DeviceConsumptionType | null;
    setDeviceConsumption: (consumption: DeviceConsumptionType | null) => void;

    devicePower: DevicePowerType | null;
    setDevicePower: (power: DevicePowerType | null) => void;

    deviceHealth: DeviceHealthType | null;
    setDeviceHealth: (health: DeviceHealthType | null) => void;
    selectionPrototypeState: SelectionPrototypeState;
    setSelectionPrototypeState: React.Dispatch<React.SetStateAction<SelectionPrototypeState>>;
    sharePrototypeState: SharePrototypeState;
    setSharePrototypeState: React.Dispatch<React.SetStateAction<SharePrototypeState>>;
}

const ReportContext = createContext<ReportContextType | undefined>(undefined);

const findAnalyticsSeries = (
    analyticsDataset: AnalyticsDataset,
    aliases: string[]
): AnalyticsSeries | null => {
    return (
        analyticsDataset.series.find((series) => {
            const normalizedKey = normalizeAnalyticsKey(series.key);
            return aliases.some((alias) => normalizedKey === normalizeAnalyticsKey(alias));
        }) || null
    );
};

const findLatestMeasurement = (
    analyticsDataset: AnalyticsDataset,
    aliases: string[]
): { key: string; value: any } | null => {
    const rows = analyticsDataset.rows as Array<Record<string, any>> | undefined;
    const measurements = rows?.[0]?.measurements as Record<string, any> | undefined;
    if (!measurements) {
        return null;
    }

    const matchedKey = Object.keys(measurements).find((key) =>
        aliases.some((alias) => normalizeAnalyticsKey(key) === normalizeAnalyticsKey(alias))
    );

    if (!matchedKey) {
        return null;
    }

    const matchedValue = measurements[matchedKey];
    if (typeof matchedValue === "object" && matchedValue !== null && "value" in matchedValue) {
        return { key: matchedKey, value: matchedValue.value };
    }

    return { key: matchedKey, value: matchedValue };
};

const coerceNumericValue = (value: unknown): number | null => {
    if (typeof value === "number" && !Number.isNaN(value)) {
        return value;
    }
    if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
};

const scopeCenterPointTables = new Set<ScopeLevel>(["venues", "events", "zones"]);

export const ReportProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [tableName, setTableName] = useState("");
    const [keyProperty, setKeyProperty] = useState("");
    const [filters, setFilters] = useState<any[]>([]);
    const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
    const [view, setView] = useState("both-row");
    const [reportTab, setReportTab] = useState("selection");
    const [reportData, setReportData] = useState<any[]>([]);
    const [columns, setColumns] = useState<ReportColumn[]>([]);
    const [analyticsDataset, setAnalyticsDataset] = useState<AnalyticsDataset | null>(null);
    const [analyticsDatasetLoading, setAnalyticsDatasetLoading] = useState<boolean>(false);
    const [analyticsDatasetError, setAnalyticsDatasetError] = useState<string | null>(null);
    const [analyticsWeatherDataset, setAnalyticsWeatherDataset] = useState<AnalyticsDataset | null>(null);
    const [analyticsWeatherDatasetLoading, setAnalyticsWeatherDatasetLoading] = useState<boolean>(false);
    const [analyticsWeatherDatasetError, setAnalyticsWeatherDatasetError] = useState<string | null>(null);
    const [analyticsRollupDataset, setAnalyticsRollupDataset] = useState<AnalyticsDataset | null>(null);
    const [analyticsRollupDatasetLoading, setAnalyticsRollupDatasetLoading] = useState<boolean>(false);
    const [analyticsRollupDatasetError, setAnalyticsRollupDatasetError] = useState<string | null>(null);
    const [analyticsGroupedDataset, setAnalyticsGroupedDataset] = useState<AnalyticsDataset | null>(null);
    const [analyticsGroupedDatasetLoading, setAnalyticsGroupedDatasetLoading] = useState<boolean>(false);
    const [analyticsGroupedDatasetError, setAnalyticsGroupedDatasetError] = useState<string | null>(null);
    const [analyticsGroupedQuery, setAnalyticsGroupedQuery] = useState({
        metric: "Consumption",
        group_by: "instance" as const,
        interval: "hour" as const,
        aggregation: "avg" as const,
    });
    const [groupedPowerMetric, setGroupedPowerMetric] = useState("Fuel Remaining");
    const [deviceLocation, setDeviceLocation] = useState<DeviceLocationType | null>(null);
    const [deviceVoltage, setDeviceVoltage] = useState<DeviceVoltageType | null>(null);
    const [deviceWeather, setDeviceWeather] = useState<DeviceWeatherType | null>(null);
    const [deviceConsumption, setDeviceConsumption] = useState<DeviceConsumptionType | null>(null);
    const [devicePower, setDevicePower] = useState<DevicePowerType | null>(null);
    const [deviceHealth, setDeviceHealth] = useState<DeviceHealthType | null>(null);
    const [selectionPrototypeState, setSelectionPrototypeState] =
        useState<SelectionPrototypeState>(DEFAULT_SELECTION_PROTOTYPE_STATE);
    const [sharePrototypeState, setSharePrototypeState] =
        useState<SharePrototypeState>(DEFAULT_SHARE_PROTOTYPE_STATE);

    const contextValue: ReportContextType = {
        tableName,
        setTableName,
        keyProperty,
        setKeyProperty,
        filters,
        setFilters,
        visibleColumns,
        setVisibleColumns,
        view,
        setView,
        reportTab,
        setReportTab,
        reportData,
        setReportData,
        columns,
        setColumns,
        analyticsDataset,
        setAnalyticsDataset,
        analyticsDatasetLoading,
        setAnalyticsDatasetLoading,
        analyticsDatasetError,
        setAnalyticsDatasetError,
        analyticsWeatherDataset,
        setAnalyticsWeatherDataset,
        analyticsWeatherDatasetLoading,
        setAnalyticsWeatherDatasetLoading,
        analyticsWeatherDatasetError,
        setAnalyticsWeatherDatasetError,
        analyticsRollupDataset,
        setAnalyticsRollupDataset,
        analyticsRollupDatasetLoading,
        setAnalyticsRollupDatasetLoading,
        analyticsRollupDatasetError,
        setAnalyticsRollupDatasetError,
        analyticsGroupedDataset,
        setAnalyticsGroupedDataset,
        analyticsGroupedDatasetLoading,
        setAnalyticsGroupedDatasetLoading,
        analyticsGroupedDatasetError,
        setAnalyticsGroupedDatasetError,
        analyticsGroupedQuery,
        setAnalyticsGroupedQuery,
        groupedPowerMetric,
        setGroupedPowerMetric,
        deviceLocation,
        setDeviceLocation,
        deviceVoltage,
        setDeviceVoltage,
        deviceWeather,
        setDeviceWeather,
        deviceConsumption,
        setDeviceConsumption,
        devicePower,
        setDevicePower,
        deviceHealth,
        setDeviceHealth,
        selectionPrototypeState,
        setSelectionPrototypeState,
        sharePrototypeState,
        setSharePrototypeState,
    };

    const devicePowerChartRef = useRef<ChartComponent | null>(null);
    const deviceConsumptionChartRef = useRef<ChartComponent | null>(null);

    useEffect(() => {
        if (analyticsDataset) {
            const statusBreakdown =
                (analyticsDataset.metadata?.status_breakdown as Record<string, number> | undefined) ?? {};
            const analyticsRows = (analyticsDataset.rows as Array<Record<string, any>> | undefined) ?? [];

            const voltageSeries = findAnalyticsSeries(analyticsDataset, [
                "voltage",
                "source_voltage",
                "source voltage",
                "smart_plug_voltage",
            ]);
            const voltagePoints = voltageSeries?.points
                .map((point) => Number(point.value))
                .filter((value) => !Number.isNaN(value)) ?? [];

            const temperatureMeasurement = findLatestMeasurement(analyticsDataset, ["temperature", "temp"]);
            const rainfallMeasurement = findLatestMeasurement(analyticsDataset, ["rainfall", "rain", "precipitation"]);
            const windSpeedMeasurement = findLatestMeasurement(analyticsDataset, ["wind_speed", "wind speed", "windspeed"]);
            const windAngleMeasurement = findLatestMeasurement(analyticsDataset, ["wind_angle", "wind angle", "wind_degree", "winddegree"]);
            const windDirectionMeasurement = findLatestMeasurement(analyticsDataset, ["wind_direction", "wind direction", "wind_dir", "winddir"]);

            const consumptionCurrentMeasurement = findLatestMeasurement(analyticsDataset, [
                "consumption",
                "consumption_current",
                "consumption current",
                "power",
                "power_w",
            ]);
            const consumptionHourMeasurement = findLatestMeasurement(analyticsDataset, ["consumption_hour", "consumption hour"]);
            const consumptionDayMeasurement = findLatestMeasurement(analyticsDataset, ["consumption_day", "consumption day"]);
            const consumptionMonthMeasurement = findLatestMeasurement(analyticsDataset, [
                "consumption_month",
                "consumption month",
                "energy",
                "energy_kwh",
            ]);
            const currentMeasurement = findLatestMeasurement(analyticsDataset, [
                "current",
                "current_a",
                "current_ma",
            ]);

            const powerRunningMeasurement = findLatestMeasurement(analyticsDataset, ["running", "is_running", "engine_running"]);
            const powerFuelUsedMeasurement = findLatestMeasurement(analyticsDataset, ["fuel_used", "fuel used"]);
            const powerFuelRemainingMeasurement = findLatestMeasurement(analyticsDataset, ["fuel_remaining", "fuel remaining"]);
            const powerRuntimeEstimateMeasurement = findLatestMeasurement(analyticsDataset, [
                "runtimeEstimateHours",
                "runtime_estimate_hours",
                "runtime estimate hours",
            ]);
            const longitudeMeasurement = findLatestMeasurement(analyticsDataset, ["longitude", "long", "lon"]);
            const latitudeMeasurement = findLatestMeasurement(analyticsDataset, ["latitude", "lat"]);
            const latestRow = analyticsRows[0];
            const latestDeviceLabel =
                (typeof latestRow?.device_label === "string" && latestRow.device_label) ||
                (typeof latestRow?.instance_name === "string"
                    ? latestRow.instance_name
                    : null);

            const analyticsLocation: DeviceLocationType = {
                hasLocation: coerceNumericValue(longitudeMeasurement?.value) !== null && coerceNumericValue(latitudeMeasurement?.value) !== null,
                longKeyColumn: longitudeMeasurement?.key ?? null,
                latKeyColumn: latitudeMeasurement?.key ?? null,
                long: coerceNumericValue(longitudeMeasurement?.value),
                lat: coerceNumericValue(latitudeMeasurement?.value),
                name: latestDeviceLabel,
            };

            const analyticsVoltage: DeviceVoltageType = {
                hasVoltage: voltagePoints.length > 0,
                voltageKeyColumn: voltageSeries?.key ?? null,
                latest: voltagePoints.length > 0 ? voltagePoints[voltagePoints.length - 1] : null,
                min: voltagePoints.length > 0 ? Math.min(...voltagePoints) : null,
                max: voltagePoints.length > 0 ? Math.max(...voltagePoints) : null,
                successReadings: voltagePoints.filter((value) => value >= 216 && value <= 253).length,
                failReadings: voltagePoints.filter((value) => value < 216 || value > 253).length,
                failRangeReadings: voltagePoints.filter((value) => value < 216 || value > 253).length,
                avgGood: voltagePoints.length > 0
                    ? voltagePoints.reduce((sum, value) => sum + value, 0) / voltagePoints.length
                    : null,
                deviation: null,
                totalReadings: voltagePoints.length,
                successPercentage: voltagePoints.length > 0
                    ? (voltagePoints.filter((value) => value >= 216 && value <= 253).length / voltagePoints.length) * 100
                    : 0,
                failPercentage: voltagePoints.length > 0
                    ? (voltagePoints.filter((value) => value < 216 || value > 253).length / voltagePoints.length) * 100
                    : 0,
                failRangePercentage: voltagePoints.length > 0
                    ? (voltagePoints.filter((value) => value < 216 || value > 253).length / voltagePoints.length) * 100
                    : 0,
            };

            const analyticsWeather: DeviceWeatherType = {
                hasWeather: Boolean(
                    temperatureMeasurement ||
                    rainfallMeasurement ||
                    windSpeedMeasurement ||
                    windAngleMeasurement ||
                    windDirectionMeasurement
                ),
                tempKeyColumn: temperatureMeasurement?.key ?? null,
                rainfallKeyColumn: rainfallMeasurement?.key ?? null,
                windSpeedKeyColumn: windSpeedMeasurement?.key ?? null,
                windAngleKeyColumn: windAngleMeasurement?.key ?? null,
                windDirectionKeyColumn: windDirectionMeasurement?.key ?? null,
                temp: typeof temperatureMeasurement?.value === "number" ? temperatureMeasurement.value : null,
                rainfall: typeof rainfallMeasurement?.value === "number" ? rainfallMeasurement.value : null,
                windSpeed: typeof windSpeedMeasurement?.value === "number" ? windSpeedMeasurement.value : null,
                windAngle: typeof windAngleMeasurement?.value === "number" ? windAngleMeasurement.value : null,
                windDirection: windDirectionMeasurement?.value != null ? String(windDirectionMeasurement.value) : null,
            };

            const analyticsConsumption: DeviceConsumptionType = {
                hasConsumption: Boolean(
                    consumptionCurrentMeasurement ||
                    consumptionHourMeasurement ||
                    consumptionDayMeasurement ||
                    consumptionMonthMeasurement ||
                    currentMeasurement
                ),
                chartRef: deviceConsumptionChartRef,
                consumptionNowKeyColumn: consumptionCurrentMeasurement?.key ?? null,
                consumptionHourKeyColumn: consumptionHourMeasurement?.key ?? null,
                consumptionDayKeyColumn: consumptionDayMeasurement?.key ?? null,
                consumptionMonthKeyColumn: consumptionMonthMeasurement?.key ?? null,
                currentKeyColumn: currentMeasurement?.key ?? null,
                consumptionNow: typeof consumptionCurrentMeasurement?.value === "number" ? consumptionCurrentMeasurement.value : null,
                consumptionHour: typeof consumptionHourMeasurement?.value === "number" ? consumptionHourMeasurement.value : null,
                consumptionDay: typeof consumptionDayMeasurement?.value === "number" ? consumptionDayMeasurement.value : null,
                consumptionMonth: typeof consumptionMonthMeasurement?.value === "number" ? consumptionMonthMeasurement.value : null,
                current: typeof currentMeasurement?.value === "number" ? currentMeasurement.value : null,
            };

            const analyticsPower: DevicePowerType = {
                hasPower: Boolean(
                    powerRunningMeasurement ||
                    powerFuelUsedMeasurement ||
                    powerFuelRemainingMeasurement
                ),
                chartRef: devicePowerChartRef,
                engineNumberKeyColumn: null,
                runningKeyColumn: powerRunningMeasurement?.key ?? null,
                fuelUsedKeyColumn: powerFuelUsedMeasurement?.key ?? null,
                fuelRemainingKeyColumn: powerFuelRemainingMeasurement?.key ?? null,
                runtimeEstimateKeyColumn: powerRuntimeEstimateMeasurement?.key ?? null,
                engineNumber: null,
                running: typeof powerRunningMeasurement?.value === "boolean"
                    ? powerRunningMeasurement.value
                    : typeof powerRunningMeasurement?.value === "number"
                        ? powerRunningMeasurement.value > 0
                        : null,
                fuelUsed: typeof powerFuelUsedMeasurement?.value === "number" ? powerFuelUsedMeasurement.value : null,
                fuelRemaining: typeof powerFuelRemainingMeasurement?.value === "number" ? powerFuelRemainingMeasurement.value : null,
                runtimeEstimateHours: typeof powerRuntimeEstimateMeasurement?.value === "number" ? powerRuntimeEstimateMeasurement.value : null,
            };

            const analyticsHealth: DeviceHealthType = {
                hasHealth: true,
                healthKeyColumn: null,
                latest: null,
                totalReadings: Object.values(statusBreakdown).reduce((sum, count) => sum + count, 0),
                successReadings: statusBreakdown.success ?? 0,
                failReadings: statusBreakdown.error ?? 0,
                unavailableReadings: statusBreakdown.unavailable ?? 0,
                offlineReadings: statusBreakdown.offline ?? 0,
            };

            setDeviceLocation(analyticsLocation);
            setDeviceVoltage(analyticsVoltage);
            setDeviceWeather(analyticsWeather);
            setDeviceConsumption(analyticsConsumption);
            setDevicePower(analyticsPower);
            setDeviceHealth(analyticsHealth);
            return;
        }

        setDeviceLocation(null);
        setDeviceVoltage(null);
        setDeviceWeather(null);
        setDeviceConsumption(null);
        setDevicePower(null);
        setDeviceHealth(null);

        if (!reportData.length) return;
        if (!columns.length) return;

        console.log("ReportProvider: Unconditional Render");

        const deviceLocationCopy: DeviceLocationType = {
            hasLocation: columns.some(c => c.headerName.toLowerCase() === "longitude" &&
                columns.some(c => c.headerName.toLowerCase() === "latitude")),

            longKeyColumn: null,
            latKeyColumn: null,
            long: null,
            lat: null,
            name: null,
        };

        if (deviceLocationCopy.hasLocation) {
            deviceLocationCopy.longKeyColumn = columns.find(c => c.headerName.toLowerCase() === "longitude")?.headerName || null;
            deviceLocationCopy.latKeyColumn = columns.find(c => c.headerName.toLowerCase() === "latitude")?.headerName || null;
        }

        const deviceVoltageCopy: DeviceVoltageType = {
            hasVoltage: columns.some(c => c.headerName.toLowerCase() === "voltage"),

            voltageKeyColumn: null,

            latest: null,
            min: null,
            max: null,
            successReadings: 0,
            failReadings: 0,
            failRangeReadings: 0,
            avgGood: null,
            totalReadings: 0,
            successPercentage: 0,
            failPercentage: 0,
            failRangePercentage: 0,
            deviation: null,
        };

        deviceVoltageCopy.voltageKeyColumn = (!deviceVoltageCopy.hasVoltage ? null : columns.find(c => c.headerName.toLowerCase() === "voltage")?.headerName || null);

        const deviceWeatherCopy: DeviceWeatherType = {
            hasWeather: columns.some(c => c.headerName.toLowerCase() === "temperature" ||
                columns.some(c => c.headerName.toLowerCase() === "rainfall" ||
                    columns.some(c => c.headerName.toLowerCase() === "wind speed" ||
                        columns.some(c => c.headerName.toLowerCase() === "wind angle" ||
                            columns.some(c => c.headerName.toLowerCase() === "wind direction"))))),

            tempKeyColumn: null,
            rainfallKeyColumn: null,
            windSpeedKeyColumn: null,
            windAngleKeyColumn: null,
            windDirectionKeyColumn: null,

            temp: null,
            rainfall: null,
            windSpeed: null,
            windAngle: null,
            windDirection: null,
        };

        if (deviceWeatherCopy.hasWeather) {
            deviceWeatherCopy.tempKeyColumn = columns.find(c => c.headerName.toLowerCase() === "temperature")?.headerName || null;
            deviceWeatherCopy.rainfallKeyColumn = columns.find(c => c.headerName.toLowerCase() === "rainfall")?.headerName || null;
            deviceWeatherCopy.windSpeedKeyColumn = columns.find(c => c.headerName.toLowerCase() === "wind speed")?.headerName || null;
            deviceWeatherCopy.windAngleKeyColumn = columns.find(c => c.headerName.toLowerCase() === "wind angle")?.headerName || null;
            deviceWeatherCopy.windDirectionKeyColumn = columns.find(c => c.headerName.toLowerCase() === "wind direction")?.headerName || null;
        }

        const deviceConsumptionCopy: DeviceConsumptionType = {
            hasConsumption: columns.some(c => c.headerName.toLowerCase().includes("consumption")),

            chartRef: deviceConsumptionChartRef,
            consumptionNowKeyColumn: null,
            consumptionHourKeyColumn: null,
            consumptionDayKeyColumn: null,
            consumptionMonthKeyColumn: null,
            currentKeyColumn: null,
            consumptionNow: null,
            consumptionHour: null,
            consumptionDay: null,
            consumptionMonth: null,
            current: null,
        };

        if (deviceConsumptionCopy.hasConsumption) {
            deviceConsumptionCopy.consumptionNowKeyColumn =
                columns.find(
                    c => c.headerName.toLowerCase() === "consumption" ||
                        (c.headerName.toLowerCase().includes("consumption") && c.headerName.toLowerCase().includes("current")))
                    ?.headerName || null;

            deviceConsumptionCopy.consumptionHourKeyColumn = columns.find(c => c.headerName.toLowerCase().includes("consumption") && c.headerName.toLowerCase().includes("hour"))?.headerName || null;
            deviceConsumptionCopy.consumptionDayKeyColumn = columns.find(c => c.headerName.toLowerCase().includes("consumption") && c.headerName.toLowerCase().includes("day"))?.headerName || null;
            deviceConsumptionCopy.consumptionMonthKeyColumn = columns.find(c => c.headerName.toLowerCase().includes("consumption") && c.headerName.toLowerCase().includes("month"))?.headerName || null;
            deviceConsumptionCopy.currentKeyColumn = columns.find(c => c.headerName.toLowerCase() === "current")?.headerName || null;
        }

        // this section is incomplete and only handles available columns for Instagrid

        const devicePowerCopy: DevicePowerType = {
            hasPower: columns.some(c => c.headerName.toLowerCase().includes("fuel")),

            chartRef: devicePowerChartRef,
            engineNumberKeyColumn: null,
            runningKeyColumn: null,
            fuelUsedKeyColumn: null,
            fuelRemainingKeyColumn: null,
            runtimeEstimateKeyColumn: null,
            engineNumber: null,
            running: null,
            fuelUsed: null,
            fuelRemaining: null,
            runtimeEstimateHours: null,
        };

        if (devicePowerCopy.hasPower) {
            devicePowerCopy.engineNumberKeyColumn = columns.find(c => c.headerName.toLowerCase() === "enginenumber")?.headerName || null;
            devicePowerCopy.runningKeyColumn = columns.find(c => c.headerName.toLowerCase() === "running")?.headerName || null;
            devicePowerCopy.fuelUsedKeyColumn = columns.find(c => c.headerName.toLowerCase() === "fuelused")?.headerName || null;
            devicePowerCopy.fuelRemainingKeyColumn = columns.find(c => c.headerName.toLowerCase() === "fuelremaining")?.headerName || null;
            devicePowerCopy.runtimeEstimateKeyColumn = columns.find(c => normalizeAnalyticsKey(c.headerName) === "runtimeestimatehours")?.headerName || null;
        }

        for (const row of reportData) {
            if (deviceLocationCopy.hasLocation) {

                // find first (most recent) long and lat

                if (deviceLocationCopy.longKeyColumn && !deviceLocationCopy.long) {
                    deviceLocationCopy.long = row[deviceLocationCopy.longKeyColumn];
                }
                if (deviceLocationCopy.latKeyColumn && !deviceLocationCopy.lat) {
                    deviceLocationCopy.lat = row[deviceLocationCopy.latKeyColumn];
                }
            }
            if (deviceWeatherCopy.hasWeather) {

                // find first (most recent) temp.  apply all other measurements from same row

                if (deviceWeatherCopy.tempKeyColumn && !deviceWeatherCopy.temp) {
                    deviceWeatherCopy.temp = row[deviceWeatherCopy.tempKeyColumn];

                    if (deviceWeatherCopy.rainfallKeyColumn && !deviceWeatherCopy.rainfall) {
                        deviceWeatherCopy.rainfall = row[deviceWeatherCopy.rainfallKeyColumn];
                    }
                    if (deviceWeatherCopy.windSpeedKeyColumn && !deviceWeatherCopy.windSpeed) {
                        deviceWeatherCopy.windSpeed = row[deviceWeatherCopy.windSpeedKeyColumn];
                    }
                    if (deviceWeatherCopy.windAngleKeyColumn && !deviceWeatherCopy.windAngle) {
                        deviceWeatherCopy.windAngle = row[deviceWeatherCopy.windAngleKeyColumn];
                    }
                    if (deviceWeatherCopy.windDirectionKeyColumn && !deviceWeatherCopy.windDirection) {
                        deviceWeatherCopy.windDirection = row[deviceWeatherCopy.windDirectionKeyColumn];
                    }
                }
            }

            if (deviceVoltageCopy.hasVoltage && deviceVoltageCopy.voltageKeyColumn) {

                let voltage = Number(row[deviceVoltageCopy.voltageKeyColumn]);

                deviceVoltageCopy.totalReadings++;

                if (!voltage) {
                    deviceVoltageCopy.failReadings++;
                } else {
                    deviceVoltageCopy.successReadings++;

                    if (voltage && (voltage < 216 || voltage > 253)) {
                        deviceVoltageCopy.failRangeReadings++;
                    }

                    if (!deviceVoltageCopy.latest) {
                        deviceVoltageCopy.latest = voltage;
                    }

                    if (!deviceVoltageCopy.min) {
                        deviceVoltageCopy.min = voltage;
                    } else if (voltage < deviceVoltageCopy.min) {
                        deviceVoltageCopy.min = voltage;
                    }

                    if (!deviceVoltageCopy.max) {
                        deviceVoltageCopy.max = voltage;
                    } else if (voltage > deviceVoltageCopy.max) {
                        deviceVoltageCopy.max = voltage;
                    }

                    if (!deviceVoltageCopy.avgGood) {
                        deviceVoltageCopy.avgGood = voltage;
                    } else {
                        deviceVoltageCopy.avgGood = (deviceVoltageCopy.avgGood * (deviceVoltageCopy.successReadings - 1) + voltage) / deviceVoltageCopy.successReadings;
                    }

                    deviceVoltageCopy.totalReadings = deviceVoltageCopy.successReadings + deviceVoltageCopy.failReadings;
                    deviceVoltageCopy.successPercentage = (deviceVoltageCopy.successReadings / deviceVoltageCopy.totalReadings) * 100;
                    deviceVoltageCopy.failPercentage = (deviceVoltageCopy.failReadings / deviceVoltageCopy.totalReadings) * 100;
                    deviceVoltageCopy.failRangePercentage = (deviceVoltageCopy.failRangeReadings / deviceVoltageCopy.totalReadings) * 100;
                }
            }
        }

        const deviceHealthCopy: DeviceHealthType = {
            hasHealth: true,

            healthKeyColumn: null,
            latest: null,
            totalReadings: 0,
            successReadings: 0,
            failReadings: 0,
            unavailableReadings: 0,
            offlineReadings: 0,
        };

        const countsMap = new Map<string, number>();

        for (const row of reportData) {
            countsMap.set(row.status, (countsMap.get(row.status) || 0) + 1);
        }

        deviceHealthCopy.totalReadings = reportData.length;
        deviceHealthCopy.successReadings = countsMap.get("success") || 0;
        deviceHealthCopy.failReadings = countsMap.get("error") || 0;
        deviceHealthCopy.unavailableReadings = countsMap.get("unavailable") || 0;
        deviceHealthCopy.offlineReadings = countsMap.get("offline") || 0;

        setDeviceLocation(deviceLocationCopy);
        setDeviceVoltage(deviceVoltageCopy);
        setDeviceWeather(deviceWeatherCopy);
        setDeviceConsumption(deviceConsumptionCopy);
        setDevicePower(devicePowerCopy);
        setDeviceHealth(deviceHealthCopy);

    }, [tableName, analyticsDataset, reportData, columns]);

    useEffect(() => {
        const selectedOption = selectionPrototypeState.selectedOptions[0];
        const selectedLevel = selectionPrototypeState.level;

        if (
            deviceLocation?.hasLocation ||
            !selectedOption ||
            !scopeCenterPointTables.has(selectedLevel)
        ) {
            return;
        }

        let cancelled = false;

        apiService
            .get(selectedLevel, selectedOption.id)
            .then((record) => {
                if (cancelled) {
                    return;
                }

                const centerPoint = parseScopeCenterPoint(record?.center_point);
                if (!centerPoint) {
                    return;
                }

                setDeviceLocation((current) => {
                    if (current?.hasLocation) {
                        return current;
                    }
                    return {
                        hasLocation: true,
                        longKeyColumn: null,
                        latKeyColumn: null,
                        long: centerPoint.lng,
                        lat: centerPoint.lat,
                        name:
                            (typeof record?.name === "string" && record.name) ||
                            selectedOption.label ||
                            null,
                    };
                });
            })
            .catch(() => {
                // Keep the report usable even if scope metadata lookup fails.
            });

        return () => {
            cancelled = true;
        };
    }, [
        deviceLocation?.hasLocation,
        selectionPrototypeState.level,
        selectionPrototypeState.selectedOptions,
    ]);

    return (
        <ReportContext.Provider value={contextValue}>
            {children}
        </ReportContext.Provider>
    );
};

export const useReportContext = () => {
    const context = useContext(ReportContext);
    if (!context) {
        throw new Error("useReportContext must be used within a ReportProvider");
    }
    return context;
};
